import React, { ReactElement } from 'react';
import Image from 'next/image';

import { CartProps } from '~/types/cart';
import { currencyWithCents } from '~/lib/format';

import { Loader } from '~/components/ui';
import CheckGreen from '~/public/font-awesome/check-circle-dollar.png';
import ErrorCircle from '~/public/font-awesome/exclamation-circle-red.png';

interface CreditsCollapsedProps {
  cart: CartProps;
  error: string;
  isCreditsPending: boolean;
  onClickToggle: () => void;
}

export default React.memo(function CreditsCollapsed({
  cart,
  error,
  isCreditsPending,
  onClickToggle,
}: Readonly<CreditsCollapsedProps>): ReactElement {
  const creditsApplied = cart?.credit ?? 0;
  const unusedCredit = cart?.available_credit ?? 0;
  let creditEl = null;

  if (creditsApplied) {
    creditEl = (
      <div className="flex items-center">
        <Image
          src={CheckGreen}
          alt="check"
          width={16}
          className="mr-2 inline-block"
        />
        <span className="font-bold" data-testid="credit-amount-text">
          {currencyWithCents(creditsApplied)}
        </span>
        <span>&nbsp;credits applied</span>
      </div>
    );
  } else if (unusedCredit) {
    creditEl = (
      <div
        className="flex items-center"
        data-testid="credit-collapsed-component"
      >
        <div>
          <span className="mr-2">
            {error && (
              <Image
                src={ErrorCircle}
                alt="check"
                width={16}
                className="mr-2 inline-block"
              />
            )}
            <span>You have&nbsp;</span>
            <span className="font-bold" data-testid="credit-amount-text">
              {currencyWithCents(unusedCredit)}
            </span>
            <span>&nbsp;in credits available</span>
          </span>
          <button
            onClick={onClickToggle}
            onKeyDown={onClickToggle}
            className="text-bolt hover:underline"
            disabled={isCreditsPending}
          >
            Apply to cart
            {isCreditsPending && (
              <span className="inline-block">
                <Loader size="sm" className="ml-2" />
              </span>
            )}
          </button>
          {error && <span className="ml-2 text-brick">{error}</span>}
        </div>
      </div>
    );
  }

  return <>{creditEl}</>;
});
